"use client";

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { getPathLabel } from "@/lib/activity-log-labels";
import type { LogEventViewModel } from "@/types/activity-log";

function ActivityLogDetailSheet({
  item,
  open,
  onOpenChange,
}: {
  item: LogEventViewModel | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  if (!item) return null;

  const metaEntries =
    item.metadata && typeof item.metadata === "object"
      ? Object.entries(item.metadata).filter(
          ([, v]) => v != null && typeof v !== "object",
        )
      : [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Détail de l&apos;événement</SheetTitle>
          <SheetDescription>
            {item.actionLabel} –{" "}
            {format(new Date(item.createdAt), "PPP p", { locale: fr })}
          </SheetDescription>
        </SheetHeader>
        <div className="space-y-4 overflow-y-auto p-5 py-4">
          <dl className="space-y-2 text-sm">
            <div>
              <dt className="text-muted-foreground font-bold">Date / heure</dt>
              <dd>
                {format(new Date(item.createdAt), "PPP 'à' p", { locale: fr })}
              </dd>
            </div>
            {item.path != null && (
              <div>
                <dt className="text-muted-foreground font-bold">Parcours</dt>
                <dd>{getPathLabel(item.path)}</dd>
              </div>
            )}
            {item.actionType != null && (
              <div>
                <dt className="text-muted-foreground font-bold">
                  Type d&apos;action
                </dt>
                <dd>{item.actionLabel}</dd>
              </div>
            )}
            {item.result != null && (
              <div>
                <dt className="text-muted-foreground font-bold">Résultat</dt>
                <dd>{item.resultLabel}</dd>
              </div>
            )}
            <div>
              <dt className="text-muted-foreground font-bold">Utilisateur</dt>
              <dd>{item.userDisplay}</dd>
              {item.userSecondaryId && (
                <dd className="mt-0.5 text-sm">
                  <span className="text-muted-foreground font-bold">
                    Identifiant utilisateur:{" "}
                  </span>
                  <span>{item.userSecondaryId}</span>
                </dd>
              )}
            </div>
            {item.kvps && (
              <div>
                <dt className="text-muted-foreground font-bold">KVPS</dt>
                <dd>{item.kvps}</dd>
              </div>
            )}
            {item.targetRef != null && (
              <div>
                <dt className="text-muted-foreground font-bold">
                  Référence(s)
                </dt>
                <dd className="font-mono text-xs break-all">
                  {Array.isArray(item.targetRef)
                    ? item.targetRef.join(", ")
                    : item.targetRef}
                </dd>
              </div>
            )}
            {item.detailsPreview && (
              <div>
                <dt className="text-muted-foreground font-bold">Détails</dt>
                <dd>{item.detailsPreview}</dd>
              </div>
            )}
          </dl>
          {metaEntries.length > 0 && (
            <div>
              <h4 className="text-muted-foreground mb-2 text-sm font-bold">
                Métadonnées
              </h4>
              <dl className="space-y-1 text-sm">
                {metaEntries.map(([key, value]) => (
                  <div key={key}>
                    <dt className="text-muted-foreground inline font-bold">
                      {key}:{" "}
                    </dt>
                    <dd className="inline">{String(value)}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export { ActivityLogDetailSheet };
